import { convertMoneyToDefaultCurrency } from '@/helper/util/common';
import { CURRENCY_DEFAULT, AACheckoutConst } from '@/helper/constants';

const BUNDLE_COLORS = {
  VP: 'yellow',
  PF: 'pink',
  PM: 'purple',
  VPL1: 'blue',
  VPL2: 'blue',
};

const BUNDLE_KEY_MAPPING = {
  VP: 'valuePack',
  PF: 'premiumFlex',
  PM: 'premiumFlatbed',
  VPL1: 'valuePackLite1',
  VPL2: 'valuePackLite2',
};

const ICON_TYPE_MAPPING = {
  luggage: 'LuggageIcon',
  meals: 'RestaurantIcon',
  seat: 'AirLineSeatReclineExtraIcon',
  flatbed: 'AirlineSeatIndividualSuiteIcon',
  calendar: 'EditCalendarIcon',
  express: 'DoubleArrowIcon',
  insurance: 'HealthSafetyIcon',
  lounge: 'EventSeatIcon',
};

type SsrType =
  | AACheckoutConst.BundleAncillaryTypeEnum.MEALS
  | AACheckoutConst.BundleAncillaryTypeEnum.SEAT
  | AACheckoutConst.BundleAncillaryTypeEnum.BAGGAGE;

const getIconByText = (text: string): string => {
  if (text.includes('行李')) return ICON_TYPE_MAPPING.luggage;
  if (text.includes('餐')) return ICON_TYPE_MAPPING.meals;
  if (text.includes('座位')) return ICON_TYPE_MAPPING.seat;
  if (text.includes('时间') || text.includes('日期'))
    return ICON_TYPE_MAPPING.calendar;
  if (text.includes('快速')) return ICON_TYPE_MAPPING.express;
  if (text.includes('保险')) return ICON_TYPE_MAPPING.insurance;
  if (text.includes('贵宾室') || text.includes('休息室'))
    return ICON_TYPE_MAPPING.lounge;
  return ICON_TYPE_MAPPING.seat;
};

export class BundleParser {
  private pageData;
  private conversionRates;
  private guests: number;

  constructor(pageData, conversionRates, guests: number) {
    this.pageData = pageData;
    this.conversionRates = conversionRates;
    this.guests = guests;
  }

  private getBundleNames(
    departAncillaryList: any[],
    returnAncillaryList: any[],
  ) {
    const bundleNamesObj = {};
    departAncillaryList.forEach((item) => {
      if (item.bundleName) {
        bundleNamesObj[item.bundleName] = true;
      }
    });
    returnAncillaryList.forEach((item) => {
      if (item.bundleName) {
        bundleNamesObj[item.bundleName] = true;
      }
    });
    return Object.keys(bundleNamesObj);
  }

  private prepareBundleData(journeys: any[]) {
    const { ancillarylist: departAncillaryList, designator: departDesignator } =
      journeys[0];
    const returnJourney = journeys[1];
    const returnAncillaryList = returnJourney?.ancillarylist || [];

    // Create lookup objects for both departure and return bundles
    const departBundleObj = {};
    departAncillaryList.forEach((departAncillary) => {
      departBundleObj[departAncillary.bundleName] = departAncillary;
    });

    const returnBundleObj = {};
    returnAncillaryList.forEach((returnAncillary) => {
      returnBundleObj[returnAncillary.bundleName] = returnAncillary;
    });

    return {
      departAncillaryList,
      returnAncillaryList,
      departBundleObj,
      returnBundleObj,
      departDesignator,
      returnJourney,
    };
  }

  private handlePremiumFlatbedLogic(
    ancillaryList: any[],
    flight: AACheckout.BundleFlight,
  ) {
    // Check if there's only one item and current flight is Premium Flatbed with price
    if (
      ancillaryList.length === 1 &&
      flight.bundleName === AACheckoutConst.FLATBED_BUNDLE_NAME &&
      flight.price.price > 0
    ) {
      flight.disableSelect = true;
      flight.isIncluded = true;
      flight.price.price = 0;
    }
    return flight;
  }

  private isDowngradable(ancillary: any): boolean {
    return !!ancillary.bundleCode && ancillary.totalPrice !== 0;
  }

  private sortBundleDataByIncluded(
    bundleData: AACheckout.BundleItem[],
  ): AACheckout.BundleItem[] {
    return bundleData.sort((a, b) => {
      const aHasIncluded =
        (a.departFlight?.isIncluded || a.returnFlight?.isIncluded) ?? false;
      const bHasIncluded =
        (b.departFlight?.isIncluded || b.returnFlight?.isIncluded) ?? false;

      if (aHasIncluded && !bHasIncluded) return -1;
      if (!aHasIncluded && bHasIncluded) return 1;
      return 0;
    });
  }

  public parse() {
    const { ancillary: originalAncillary = {} } = this.pageData;
    const { bundle = {} } = originalAncillary;
    const {
      isPaxDependent,
      measure,
      startPrice,
      metaData,
      data: journeys,
    } = bundle;
    const { bundleCard: bundleMetaItems, compareBundle } = metaData;
    const bundleData: AACheckout.BundleItem[] = [];

    // prepare base data
    const {
      departAncillaryList,
      returnAncillaryList,
      departBundleObj,
      returnBundleObj,
      departDesignator,
      returnJourney,
    } = this.prepareBundleData(journeys);

    // get all unique bundle names
    const allBundleNames = this.getBundleNames(
      departAncillaryList,
      returnAncillaryList,
    );

    // handle each bundle
    for (const bundleName of allBundleNames) {
      const departAncillary = departBundleObj[bundleName] || {};
      const returnAncillary = returnBundleObj[bundleName] || {};

      const bundleMeta = bundleMetaItems.find(
        (f) =>
          f.bundleId === (departAncillary.bundleId || returnAncillary.bundleId),
      );

      // For departure flight
      const isDepartDowngradable = this.isDowngradable(departAncillary);

      let departFlight: AACheckout.BundleFlight = {
        bundleCode: departAncillary.bundleCode,
        bundleId: departAncillary.bundleId,
        bundleName: departAncillary.bundleName,
        designator: {
          arrivalStation: departDesignator.arrivalStation,
          departureStation: departDesignator.departureStation,
        },
        guest: this.guests,
        isAvailable: true,
        isDowngradable: isDepartDowngradable,
        includedSsrs: departAncillary.includedSsrs,
        price: {
          currency: CURRENCY_DEFAULT,
          price: departAncillary.bundleCode
            ? convertMoneyToDefaultCurrency(
                departAncillary.totalPrice,
                departAncillary.currency,
                this.conversionRates,
              )
            : 0,
        },
        type: 'depart',
      };

      // Apply Premium Flatbed logic for depart flight
      departFlight = this.handlePremiumFlatbedLogic(
        departAncillaryList,
        departFlight,
      );

      // For return flight
      let returnFlight: AACheckout.BundleFlight = null;
      let isReturnDowngradable = false;
      if (returnJourney) {
        isReturnDowngradable = this.isDowngradable(returnAncillary);

        returnFlight = {
          bundleCode: returnAncillary.bundleCode,
          bundleId: returnAncillary.bundleId,
          bundleName: returnAncillary.bundleName,
          designator: {
            arrivalStation: returnJourney.designator.arrivalStation,
            departureStation: returnJourney.designator.departureStation,
          },
          guest: this.guests,
          isAvailable: true,
          isDowngradable: isReturnDowngradable,
          includedSsrs: returnAncillary.includedSsrs,
          price: {
            currency: CURRENCY_DEFAULT,
            price: returnAncillary.bundleCode
              ? convertMoneyToDefaultCurrency(
                  returnAncillary.totalPrice,
                  returnAncillary.currency,
                  this.conversionRates,
                )
              : 0,
          },
          type: 'return',
        };

        // Apply Premium Flatbed logic for return flight
        returnFlight = this.handlePremiumFlatbedLogic(
          returnAncillaryList,
          returnFlight,
        );
      }

      // Skip adding to bundleData if both flights are not downgradable
      if (!isDepartDowngradable && !isReturnDowngradable) {
        continue;
      }

      bundleData.push({
        color:
          BUNDLE_COLORS[departAncillary.bundleId || returnAncillary.bundleId] ||
          '',
        content: bundleMeta.body.map((item) => ({
          icon: item.icon || getIconByText(item.text),
          iconColor:
            BUNDLE_COLORS[
              departAncillary.bundleId || returnAncillary.bundleId
            ] || '',
          text: item.text,
        })),
        departFlight,
        icon: 'CheckIcon',
        isMobile: true,
        returnFlight,
        subTitle: bundleMeta.header.subtitle,
        title: bundleMeta.header.title,
      });
    }

    // Sort bundleDatad
    this.sortBundleDataByIncluded(bundleData);

    return {
      startPrice,
      isPaxDependent,
      measure,
      bundleData,
      comparisonData: this.parseComparisonData(compareBundle, bundleMetaItems),
      originBundleData: {
        departAncillaryList,
        returnAncillaryList,
      },
    };
  }

  private parseComparisonData(compareBundle, bundleMetaItems) {
    const comparisonData = {
      columns: [
        {
          columnKey: 'inclusion',
          columnName: '套餐',
          columnSubheader: 'Each pax',
        },
      ],
      data: [],
    };

    // generate columns
    compareBundle.forEach((bundle) => {
      const bundleMetaItem = bundleMetaItems.find(
        (metaItem) =>
          metaItem.bundleId.toLowerCase() === bundle.bundleId.toLowerCase(),
      );
      const columnKey =
        BUNDLE_KEY_MAPPING[bundle.bundleId] || bundle.bundleId.toLowerCase();

      comparisonData.columns.push({
        columnKey,
        columnName: bundleMetaItem.header.title,
        columnSubheader: '',
      });

      bundle.body.forEach((item) => {
        const inclusionKey = item.inclusion;
        let comparisonItem = comparisonData.data.find(
          (d) => d.inclusion === inclusionKey,
        );

        const isIcon = item.type === 'icon';
        if (!comparisonItem) {
          comparisonItem = {
            comparisonType: isIcon ? 'icon' : 'text',
            inclusion: inclusionKey,
            inclusionIcon: item.inclusionIcon || getIconByText(item.inclusion),
            inclusionIconColor: 'charcoalGrey',
            inclusionIconPosition: 'Prefix',
            CheckCircleOutlineIconColor: isIcon ? 'charcoalGrey' : '',
          };
          comparisonData.data.push(comparisonItem);
        }

        const columnKey =
          BUNDLE_KEY_MAPPING[bundle.bundleId] || bundle.bundleId.toLowerCase();
        comparisonItem[columnKey] = item.text;
      });
    });

    return comparisonData;
  }

  public processIncludedSsrs(
    flight: AACheckout.BundleFlight,
    flightKey: AACheckoutConst.FlightKeyEnum,
    ancillaries: {
      [key in AACheckoutConst.BundleAncillaryTypeEnum]?: AACheckout.BundleInfluenceData;
    },
  ) {
    flight?.includedSsrs?.forEach((ssr) => {
      if (!ancillaries[ssr.ancillaryType]) {
        ancillaries[ssr.ancillaryType] = {
          departFlight: null,
          returnFlight: null,
        };
      }

      const filteredFlight = {
        ...flight,
        includedSsrs: flight.includedSsrs.filter(
          (includedSsr) => includedSsr.ancillaryType === ssr.ancillaryType,
        ),
      };

      ancillaries[ssr.ancillaryType][flightKey] = filteredFlight;
    });
  }
}

type PlainAddonItemMap = {
  [key in AACheckoutConst.BundleAncillaryTypeEnum]?: AACheckout.PlainAddonItem[];
};

export class PlainAddonItemsGenerator {
  paxTitles: AACheckout.PaxTitle[];
  plainAddonItemMap: PlainAddonItemMap;
  selectedBundleData: any;

  validationResult = {
    meals: true,
    seat: true,
  };

  constructor(
    paxTitles: AACheckout.PaxTitle[],
    plainAddonItemMap: PlainAddonItemMap,
    selectedBundleData: any,
  ) {
    this.paxTitles = paxTitles;
    this.plainAddonItemMap = plainAddonItemMap;
    this.selectedBundleData = selectedBundleData;
  }

  getSelectedAddon(
    type: SsrType,
    departureStation: string,
    arrivalStation: string,
    passengerReferenceId: string,
  ) {
    return this.plainAddonItemMap[type]?.find(
      (m) =>
        m.departureStation === departureStation &&
        m.arrivalStation === arrivalStation &&
        m.passengerReferenceId === passengerReferenceId,
    );
  }

  generateSsrsByType(
    type: SsrType,
    ssrs: AACheckout.BundleIncludedSsr[],
    paxTitle: AACheckout.PaxTitle,
  ) {
    if (!ssrs || ssrs.length === 0) {
      return [];
    }
    const firstSsr = ssrs.shift();

    firstSsr.journeyDetails = firstSsr.journeyDetails.map((j) => {
      const selectedAddon = this.getSelectedAddon(
        type,
        j.designator.origin,
        j.designator.destination,
        paxTitle.paxId,
      );

      if (!selectedAddon && firstSsr.mandatory) {
        this.validationResult[type] = false;
      }

      return {
        ...j,
        selectedSsrs: this.createSelectedSsrs(type, selectedAddon),
      };
    });

    return [firstSsr, ...ssrs];
  }

  createSelectedSsrs(type: SsrType, selectedAddon: any) {
    if (!selectedAddon) return [];

    switch (type) {
      case AACheckoutConst.BundleAncillaryTypeEnum.SEAT:
        return [
          {
            code: selectedAddon.id.toLowerCase(),
            name: selectedAddon.properties?.seatNumber,
          },
        ];

      case AACheckoutConst.BundleAncillaryTypeEnum.BAGGAGE: {
        const weight = selectedAddon.properties?.baggageDimension?.[0]?.weight;

        const name = weight ? `${weight.value} ${weight.unit}` : '';

        return [
          {
            code: selectedAddon.id,
            name,
          },
        ];
      }

      case AACheckoutConst.BundleAncillaryTypeEnum.MEALS:
        return [
          {
            code: selectedAddon.id,
            name: selectedAddon.name,
          },
        ];
    }
  }

  generateSsrs(
    ssrs: AACheckout.BundleIncludedSsr[],
    paxTitle: AACheckout.PaxTitle,
  ) {
    const convertedSrrs: AACheckout.BundleIncludedSsr[] = ssrs.map((ssr) => {
      return {
        ssrCode: ssr.ssrCode,
        ssrName: ssr.ssrName,
        ancillaryType: ssr.ancillaryType,
        mandatory: ssr.mandatory,
        journeyDetails: ssr.journeyDetails.map((j) => ({
          designator: j.designator,
          selectedSsrs: [],
          limitPerPassenger: j.limitPerPassenger,
        })),
      };
    }) as any;

    const handledTypes: SsrType[] = [
      AACheckoutConst.BundleAncillaryTypeEnum.MEALS,
      AACheckoutConst.BundleAncillaryTypeEnum.SEAT,
      AACheckoutConst.BundleAncillaryTypeEnum.BAGGAGE,
    ];

    const processedSsrs = handledTypes.flatMap((type) =>
      this.generateSsrsByType(
        type,
        convertedSrrs.filter((s) => s.ancillaryType === type),
        paxTitle,
      ),
    );

    const otherSsrs = convertedSrrs.filter(
      (s) => !handledTypes.includes(s.ancillaryType as SsrType),
    );

    return [...processedSsrs, ...otherSsrs];
  }

  generateByJourney(bundleFlight: AACheckout.BundleFlight) {
    if (!bundleFlight || !bundleFlight.bundleCode) {
      return {
        plainAddonItemsForCheckout: [],
        plainAddonItems: [],
      };
    }

    const plainAddonItemsForCheckout: AACheckout.PlainAddonItem[] = [];
    const plainAddonItems: AACheckout.PlainAddonItem[] = [];
    const allIncludedSsrs: AACheckout.BundleIncludedSsr[] = [];

    for (const paxTitle of this.paxTitles) {
      const { includedSsrs } = bundleFlight;
      const finalSsrs = this.generateSsrs(includedSsrs, paxTitle);

      plainAddonItemsForCheckout.push({
        id: bundleFlight.bundleCode,
        passengerReferenceId: paxTitle.paxId,
        category: 'Bundle',
        groupBy: 'Bundle',
        currency: bundleFlight.price.currency,
        name: bundleFlight.bundleName,
        price: bundleFlight.price.price,
        type: 'bundle',
        departureStation: bundleFlight.designator.departureStation,
        arrivalStation: bundleFlight.designator.arrivalStation,
        properties: {
          bundleCode: bundleFlight.bundleCode,
          bundleName: bundleFlight.bundleName,
          includedSsrs: finalSsrs,
        },
      });

      allIncludedSsrs.push(...finalSsrs);
    }

    plainAddonItems.push({
      id: bundleFlight.bundleCode,
      passengerReferenceId: '',
      category: 'Bundle',
      groupBy: 'Bundle',
      currency: bundleFlight.price.currency,
      name: `${bundleFlight.bundleName} 适用${this.paxTitles.length}名乘客`,
      price: bundleFlight.price.price,
      type: 'bundle',
      departureStation: bundleFlight.designator.departureStation,
      arrivalStation: bundleFlight.designator.arrivalStation,
      properties: {
        bundleCode: bundleFlight.bundleCode,
        bundleName: bundleFlight.bundleName,
        includedSsrs: allIncludedSsrs,
      },
    });

    return { plainAddonItemsForCheckout, plainAddonItems };
  }

  generate() {
    const {
      plainAddonItemsForCheckout: departPlainAddonItemsForCheckout,
      plainAddonItems: departPlainAddonItems,
    } = this.generateByJourney(this.selectedBundleData?.departFlight);
    const {
      plainAddonItemsForCheckout: returnPlainAddonItemsForCheckout,
      plainAddonItems: returnPlainAddonItems,
    } = this.generateByJourney(this.selectedBundleData?.returnFlight);

    console.log('***meals and seat validationResult***', this.validationResult);

    return {
      plainAddonItemsForCheckout: departPlainAddonItemsForCheckout.concat(
        returnPlainAddonItemsForCheckout,
      ),
      plainAddonItems: departPlainAddonItems.concat(returnPlainAddonItems),
    };
  }
}
